import * as React from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n';
import {
  getElasticCategoryProductInfo,
  getProductConnections,
  resolveShortProductUrl,
} from '@bart.sk-eshop/eshop-defaults/lib/utilities/selectors';
import { getProductGoodsGroupedAttribs } from '@bart.sk-eshop/eshop-defaults/lib/components/Golfcentrum/selectors';
import { BigButtonWithRequest } from '@bart.sk-eshop/eshop-defaults/lib/components/Golfcentrum/Product/BigButtonWithRequest';
import {
  Picker as AttribPicker,
  PickerType,
} from '@bart.sk-eshop/eshop-defaults/lib/components/Golfcentrum/Attrib/Picker';
import { Picker as VariantPicker } from '@bart.sk-eshop/eshop-defaults/lib/components/Golfcentrum/ProductVariants/Picker';
import { prop } from '../../utilities';

interface Props {
  product: any;
  addToCart?: (product: any) => void;
  addToRequest?: (product: any) => void;
}

function ProductTabSlider({ product, addToCart, addToRequest }: Props) {
  const [selectedProduct, setSelectedProduct] = React.useState<any>(product);
  const [mainProduct, setMainProduct] = React.useState<any>(
    product.product_detail,
  );

  const {
    name,
    productName,
    brand,
    price,
    oldPrice,
    onStock,
    unit,
    picture,
    currency,
    mainGoodAvailability,
  } = getElasticCategoryProductInfo(selectedProduct);

  const url = resolveShortProductUrl(selectedProduct);

  const isOrderable =
    prop(mainGoodAvailability, 'avail_uniqid', null) === 'INSHOP'
      ? false
      : true;

  const connections = getProductConnections(product);
  const cVariants: any =
    selectedProduct && connections && connections.length > 0
      ? [...connections].sort((a, b) => (a.product_id > b.product_id ? 1 : -1))
      : [];

  const { groupedAttribs } = getProductGoodsGroupedAttribs(mainProduct);

  let hasValues = false;
  Object.keys(groupedAttribs).forEach(key => {
    const groupedAttrib = groupedAttribs[key];
    if (
      groupedAttrib &&
      groupedAttrib.values &&
      groupedAttrib.values.length > 0
    ) {
      const validValue = groupedAttrib.values.find(a => a.attrib_value);
      if (validValue) {
        hasValues = true;
      }
      groupedAttribs[key].values = groupedAttrib.values.sort(
        (a, b) => a.value_sort - b.value_sort,
      );
    }
  });

  let selectedMainGood = prop(selectedProduct, 'product_detail.main_good', {});
  if (!selectedMainGood) {
    selectedMainGood = prop(selectedProduct, 'main_good', {});
  }
  const [selectedGood, setSelectedGood] = React.useState<any>(selectedMainGood);
  const [disableOrderButton, setDisableOrderButton] = React.useState<boolean>(
    false,
  );

  React.useEffect(() => {
    if (Object.keys(selectedGood).length === 0) {
      setDisableOrderButton(true);
    }
  }, [selectedGood]);

  const handleVariantSelect = (productId: any) => {
    const connectedProduct = connections.find(
      (foundedProduct: any) => foundedProduct.product_id === productId,
    );
    if (connectedProduct) {
      if (productId === product.product_id) {
        setMainProduct(product.product_detail);
      } else {
        setMainProduct(connectedProduct);
      }
      setSelectedProduct(connectedProduct);
      if (hasValues) {
        setDisableOrderButton(true);
      }
    }
  };

  const handleGoodChange = (_e: any, goodId: number) => {
    let goods = prop(selectedProduct, 'product_detail.goods', []);
    if (goods.length === 0) {
      goods = prop(selectedProduct, 'goods', []);
    }
    const good = goods.find((g: any) => g.good_id === goodId);
    setSelectedGood(good);
    setDisableOrderButton(false);
  };

  const setRequest = () => {
    if (!addToCart) {
      return;
    }
    const productCopy = { ...selectedProduct };
    productCopy.main_good = selectedGood;
    if (Object.keys(selectedGood).length > 0) {
      addToCart(productCopy);
    }
  };

  const handleRequest = () => {
    if (addToRequest) {
      addToRequest(selectedProduct);
    }
  };

  return (
    <Wrapper>
      <ImageLink to={url}>
        <Image alt={name || productName} src={picture} />
      </ImageLink>
      <InfoLink to={url}>
        <Brand>{brand}</Brand>
        <Name>{productName || name}</Name>
      </InfoLink>
      <PriceWrapper>
        {oldPrice && oldPrice > price ? (
          <OldPrice>{`${Number(oldPrice).toFixed(2)} ${currency}`}</OldPrice>
        ) : null}
        <Price>{`${Number(price || 0).toFixed(2)} ${currency}`}</Price>
        <StockText onStock={onStock > 0}>
          {onStock > 0 ? __('Skladom') : __('Nie je skladom')}
        </StockText>
      </PriceWrapper>
      {cVariants.length > 1 ? (
        <VariantsRow>
          <VariantPicker
            items={cVariants}
            currentItem={selectedProduct}
            type={PickerType.IMAGE}
            link={false}
            handleAttribSelect={handleVariantSelect}
          />
        </VariantsRow>
      ) : null}
      {Object.keys(groupedAttribs).length > 0 && hasValues ? (
        <AttribsRow>
          {Object.keys(groupedAttribs).map((key: any, i) => {
            const item = groupedAttribs[key];
            return (
              <AttribPicker
                handleItemChange={handleGoodChange}
                attrib={item}
                selectedItem={selectedGood}
                type={PickerType.VALUE}
                key={`${i}-${item.value_id}`}
              />
            );
          })}
        </AttribsRow>
      ) : null}
      {isOrderable && onStock > 0 ? (
        <ButtonRow>
          <BigButtonWithRequest
            onButtonClick={setRequest}
            onRequestClick={handleRequest}
            disabled={disableOrderButton}
            cartIsFetching={false}
          />
        </ButtonRow>
      ) : null}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${rem(240)};
  height: 100%;
  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.grayLight};
  border-radius: ${rem(2)};
  padding: ${rem(16)};
  box-sizing: border-box;
  margin: 0 ${rem(8)};
`;

const ImageLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${rem(160)};
  text-decoration: none;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const InfoLink = styled(Link)`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(12)};
  text-decoration: none;
  color: inherit;
`;

const Brand = styled.span`
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.color.gray48};
  margin-bottom: ${rem(4)};
`;

const Name = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  color: ${({ theme }) => theme.color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: ${rem(36)};
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(8)};
`;

const Price = styled.span`
  font-size: ${rem(16)};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const OldPrice = styled.span`
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.color.gray48};
  text-decoration: line-through;
`;

const StockText = styled.span<{ onStock: boolean }>`
  font-size: ${rem(11)};
  color: ${({ onStock, theme }) =>
    onStock ? theme.color.green : theme.color.gray48};
  margin-top: ${rem(2)};
`;

const VariantsRow = styled.div`
  margin-top: ${rem(12)};
`;

const AttribsRow = styled.div`
  margin-top: ${rem(8)};
`;

const ButtonRow = styled.div`
  display: flex;
  width: 100%;
  margin-top: auto;
  padding-top: ${rem(12)};

  & > div {
    width: 100%;
    max-width: 100%;
    margin-top: 0;
  }
`;

export default ProductTabSlider;
